import type { VisitItineraryPayload } from './visit-itinerary';
import { sessionToBuyerPhone } from './session';

export type AdvisorMode = 'demo' | 'live';

export function getAdvisorMode(): AdvisorMode {
  const mode = import.meta.env.VITE_ADVISOR_MODE as string | undefined;
  return mode === 'live' ? 'live' : 'demo';
}

export interface AdvisorTurnRequest {
  sessionId: string;
  text: string;
  projectId?: string;
  projectName?: string;
  preferences?: Record<string, string | undefined>;
  actionId?: string;
  /** P7 — current board tab for server context. */
  boardTab?: string;
  /**
   * Brief-phase free text: the Spine extracts constraints and returns them via
   * prefsSnapshot without searching/composing (reply is empty). Lets the funnel
   * pre-fill from natural language without a shortlist jumping the brief gate.
   */
  briefExtract?: boolean;
}

export type AdvisorNbaBoard = 'none' | 'matches' | 'project' | 'compare' | 'visit';
export type AdvisorNbaBoardTab = 'legal' | 'units' | 'price' | 'emi' | 'overview';

export interface AdvisorNba {
  chips: string[];
  /** ADR-006: per-chip action_id aligned by index with `chips`. A non-empty
   *  entry lets a tap send `action_id` so the door skips the LLM extract. */
  chip_actions?: string[];
  board: AdvisorNbaBoard;
  board_tab?: AdvisorNbaBoardTab;
  board_project_id?: string;
}

export interface AdvisorChecklistSnapshot {
  phase: string;
  focus_project_id?: string;
  focus_project_name?: string;
  engaged_project_ids: string[];
}

export interface AdvisorDimensionFit {
  dimension: string;
  score: number;
  weight: number;
  evidence: string;
  good: boolean;
}

export interface AdvisorDimensionGap {
  dimension: string;
  weight: number;
  label: string;
}

export interface AdvisorProjectCard {
  id: string;
  name: string;
  micro_market?: string;
  price_label?: string;
  starting_price_display?: string;
  source_builder_id?: string;
  /** Desk-authored trade-off narration ("✓ 17 min to ITPL · ⚠ ₹15 L over…"). */
  tradeoff_note?: string;
  /** Typed rank receipts — preferred over tradeoff_note when present. */
  dimension_fit?: AdvisorDimensionFit[];
  dimension_gap?: AdvisorDimensionGap;
}

export interface AdvisorProjectDetail {
  project_id: string;
  name: string;
  micro_market: string;
  summary?: string;
  rera_number?: string;
  possession?: string;
  project_type?: string;
  starting_price_display?: string;
  khata?: string;
  na_status?: string;
  ec_status?: string;
  loan_eligibility?: string;
  configurations?: Array<{
    unit_type: string;
    price_display: string;
    price_min_inr: number;
    /** Optional — present when ConverseSpine enrichment/listUnits includes size. */
    size_display?: string;
  }>;
  location?: {
    connectivity_summary?: string;
    micro_market_overview?: string;
    nearby_pois?: string[];
    drive_times?: string[];
  };
  faqs?: Array<{ question_key: string; question: string; answer: string }>;
  /** Approved corridor value intel (Desk micro_market_intel via ConverseSpine).
   *  Absent = no verified data — the board renders the honest gap, never the
   *  dummy analytics. Every number carries provenance. */
  market_intel?: MarketIntel;
}

/** Corridor value intel — approved rows only; nulls are honest gaps. */
export interface MarketIntel {
  micro_market_id: string;
  city: string;
  display_name: string;
  appreciation: {
    three_yr_pct: number | null;
    five_yr_pct: number | null;
    corridor_maturity: string | null;
  };
  rent_bands: Array<{ unit_type?: string; rent_min_inr?: number; rent_max_inr?: number }>;
  drivers: Array<{ event: string; date?: string; note?: string }>;
  provenance: { source: string; as_of: string; confidence: number };
}

export interface BookedVisit {
  projectId: string;
  projectName: string;
  label: string;
  iso: string;
}

export interface VisitQueuePayload {
  active?: { project_id: string; project_name: string };
  queued: Array<{ project_id: string; project_name: string }>;
  awaiting_confirm?: boolean;
  proposed_label?: string;
}

export type { VisitItineraryPayload } from './visit-itinerary';

export interface CompareMatrixPayload {
  projects: Array<{ project_id: string; name: string }>;
  rows: Array<{ key?: string; label: string; values: readonly string[] }>;
}

export type AdvisorUiMode =
  | 'brief_collect'
  | 'search_recovery'
  | 'preference_refine'
  | 'matches_hub'
  | 'focused';

export interface ServerSuggestedAction {
  id: string;
  label: string;
  patch: Record<string, string | undefined>;
  user_line: string;
  expected_matches: number;
}

export interface SearchRecoveryPayload {
  mode: 'search_recovery' | 'preference_refine';
  reason: string;
  constraints: Record<string, string | undefined>;
  suggested_actions: ServerSuggestedAction[];
}

export interface AdvisorMediaAttachment {
  asset_kind: string;
  label: string;
  url: string;
  mime_type?: string;
  delivery: 'image' | 'document' | 'video';
  filename?: string;
  project_name?: string;
}

export interface AdvisorTurnResponse {
  status: 'ok' | 'deduplicated' | 'error';
  reply?: string;
  /**
   * The messaging key for this buyer on the web channel — builder x buyer x
   * CHANNEL. `/api/advisor/turn` carries no project_id, so what comes back is
   * a thread, never a lead. (Was `conversationId`; the noun was retired
   * 1 Sep 2026.)
   */
  threadId?: string;
  /** Named media cards from Spine — do not scrape URLs from reply text. */
  mediaAttachments?: AdvisorMediaAttachment[];
  projects?: AdvisorProjectCard[];
  /** Spine offered-board IDs (not SPA Save favorites). */
  offeredIds?: string[];
  prefsSnapshot?: Record<string, string | undefined>;
  phase?: string;
  uiMode?: AdvisorUiMode;
  searchRecovery?: SearchRecoveryPayload;
  /**
   * The ENGINE says whether the buyer should be offered a way to adjust —
   * it widened, found nothing, or is re-offering after a rejection. The SPA
   * used to infer this by regexing the reply text for "no exact match" and
   * even place names; reword one sentence and the refine chips vanished
   * silently. See ConverseSpine map-response.ts.
   */
  suggestRefine?: boolean;
  goalKind?: string;
  goalTopic?: string;
  goalProjectId?: string;
  focusedProject?: AdvisorProjectDetail;
  visitBooked?: BookedVisit;
  visitQueue?: VisitQueuePayload;
  visitItinerary?: VisitItineraryPayload;
  compareMatrix?: CompareMatrixPayload;
  nba?: AdvisorNba;
  checklistSnapshot?: AdvisorChecklistSnapshot;
  error?: string;
}

export interface AdvisorBriefFacetsResponse {
  status: 'ok' | 'error';
  builder_id?: string;
  locations?: string[];
  bhk?: string[];
  property_types?: string[];
  plot_sizes?: string[];
  project_count?: number;
  error?: string;
}

const DEFAULT_BUILDER = import.meta.env.VITE_ADVISOR_BUILDER_ID ?? 'naya-advisor';

/**
 * Hard ceiling on a single turn. A request that never settles must not leave the
 * app spinning forever — the Cowork F2 freeze that survived reloads. On timeout
 * the fetch aborts, we return status:'error', and the caller's existing handler
 * shows the reachability bubble and clears its loading flag.
 *
 * A full search+rerank+compose turn measures ~7s against dev; 20s leaves headroom
 * for a cold Worker/proxy start so a slow-but-working turn is never clipped, while
 * still bounding a genuinely hung request to a recoverable error.
 */
const TURN_TIMEOUT_MS = 20_000;

export async function sendAdvisorTurn(
  req: AdvisorTurnRequest,
  signal?: AbortSignal,
): Promise<AdvisorTurnResponse> {
  const base = import.meta.env.VITE_API_BASE ?? '/api';

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TURN_TIMEOUT_MS);
  // Forward a caller-supplied signal into the same controller so both an
  // explicit cancel and the timeout land on one abort path.
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  let res: Response;
  try {
    res = await fetch(`${base}/advisor/turn`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        session_id: req.sessionId,
        text: req.text,
        buyer_phone: sessionToBuyerPhone(req.sessionId),
        builder_id: DEFAULT_BUILDER,
        ...(req.projectId ? { project_id: req.projectId } : {}),
        ...(req.projectName ? { project_name: req.projectName } : {}),
        ...(req.preferences && Object.keys(req.preferences).length > 0
          ? { preferences: req.preferences }
          : {}),
        ...(req.actionId ? { action_id: req.actionId } : {}),
        ...(req.boardTab ? { board_tab: req.boardTab } : {}),
        ...(req.briefExtract ? { brief_extract: true } : {}),
      }),
      signal: controller.signal,
    });
  } catch (err) {
    // AbortError (timeout or cancel) or a network failure — never throw into the
    // caller; return a handled error so isLoading always clears.
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    return { status: 'error', error: aborted ? 'timeout' : 'network' };
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    return {
      status: 'error',
      error: body.error ?? body.message ?? `HTTP ${res.status}`,
    };
  }

  const body = (await res.json()) as {
    status?: string;
    reply?: string;
    /** Desk's THREAD id, and only once the reveal has given this session a
     *  real buyer identity — an anonymous advisor session has no thread, so
     *  this is absent on most turns. The field ConverseSpine actually emits
     *  is `nd_thread_id` (`AdvisorTurnResponse`); `state_key` beside it is
     *  Spine's own engine key, NOT a Desk id, and must never be read as one. */
    nd_thread_id?: string;
    media_attachments?: AdvisorMediaAttachment[];
    projects?: AdvisorProjectCard[];
    shortlist?: string[];
    prefs_snapshot?: Record<string, string | undefined>;
    phase?: string;
    focused_project?: AdvisorProjectDetail;
    visit_booked?: {
      project_id: string;
      project_name: string;
      label: string;
      iso: string;
    };
    visit_queue?: VisitQueuePayload;
    visit_itinerary?: VisitItineraryPayload;
    compare_matrix?: CompareMatrixPayload;
    ui_mode?: AdvisorUiMode;
    search_recovery?: SearchRecoveryPayload;
    /** Engine-stated: offer the buyer a way to adjust. Optional here only
     *  because demo fixtures return partial bodies. */
    suggest_refine?: boolean;
    nba?: AdvisorNba;
    checklist_snapshot?: AdvisorChecklistSnapshot;
    error?: string;
    debug?: { goal?: { kind?: string; topic?: string; projectId?: string; project_id?: string } };
  };

  if (body.status === 'error') {
    return { status: 'error', error: body.error ?? 'turn_failed' };
  }

  if (body.status === 'deduplicated') {
    return { status: 'deduplicated', threadId: body.nd_thread_id };
  }

  return {
    status: 'ok',
    reply: body.reply ?? '',
    threadId: body.nd_thread_id,
    ...(body.media_attachments?.length
      ? { mediaAttachments: body.media_attachments }
      : {}),
    projects: body.projects,
    ...(body.shortlist?.length ? { offeredIds: body.shortlist } : {}),
    prefsSnapshot: body.prefs_snapshot,
    phase: body.phase,
    uiMode: body.ui_mode,
    searchRecovery: body.search_recovery,
    suggestRefine: body.suggest_refine,
    goalKind: body.debug?.goal?.kind,
    goalTopic: body.debug?.goal?.topic,
    goalProjectId:
      body.debug?.goal?.projectId ??
      body.debug?.goal?.project_id ??
      body.focused_project?.project_id,
    focusedProject: body.focused_project,
    visitBooked: body.visit_booked
      ? {
          projectId: body.visit_booked.project_id,
          projectName: body.visit_booked.project_name,
          label: body.visit_booked.label,
          iso: body.visit_booked.iso,
        }
      : undefined,
    visitQueue: body.visit_queue ?? undefined,
    visitItinerary: body.visit_itinerary ?? undefined,
    compareMatrix: body.compare_matrix,
    nba: body.nba,
    checklistSnapshot: body.checklist_snapshot,
  };
}

export interface AdvisorPreviewCard {
  project_id: string;
  name: string;
  micro_market: string;
  starting_price_display: string;
  project_type?: string;
}

export interface AdvisorPreviewResult {
  /** Honest match count against the brief so far (capped — see `capped`). */
  count: number;
  /** True when the count hit the Desk cap — render as "count+". */
  capped: boolean;
  /** False until at least one hard filter is set. */
  narrowing: boolean;
  matches: AdvisorPreviewCard[];
}

const PREVIEW_TIMEOUT_MS = 8_000;
const EMPTY_PREVIEW: AdvisorPreviewResult = { count: 0, capped: false, narrowing: false, matches: [] };

/**
 * Live narrowing preview for the thinking-strip — a cheap, side-effect-free
 * count of the catalog against the brief so far. Never throws: an abort
 * (superseded by a newer brief), a timeout, or a network fault all resolve to
 * "no narrowing yet" so the strip degrades to the plain typing indicator.
 */
export async function fetchAdvisorPreview(
  preferences: Record<string, string | undefined>,
  signal?: AbortSignal,
): Promise<AdvisorPreviewResult> {
  const base = import.meta.env.VITE_API_BASE ?? '/api';

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PREVIEW_TIMEOUT_MS);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const res = await fetch(`${base}/advisor/preview`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ builder_id: DEFAULT_BUILDER, preferences }),
      signal: controller.signal,
    });
    if (!res.ok) return EMPTY_PREVIEW;
    const body = (await res.json()) as Partial<AdvisorPreviewResult> & { status?: string };
    return {
      count: body.count ?? 0,
      capped: body.capped ?? false,
      narrowing: body.narrowing ?? false,
      matches: body.matches ?? [],
    };
  } catch {
    return EMPTY_PREVIEW;
  } finally {
    clearTimeout(timeout);
  }
}

export async function fetchAdvisorProjectDetail(
  sessionId: string,
  projectId: string,
  signal?: AbortSignal,
): Promise<{ status: 'ok' | 'error'; project?: AdvisorProjectDetail; error?: string }> {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  const qs = new URLSearchParams({
    session_id: sessionId,
    project_id: projectId,
    buyer_phone: sessionToBuyerPhone(sessionId),
    builder_id: DEFAULT_BUILDER,
  });
  const res = await fetch(`${base}/advisor/project?${qs}`, { signal });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return { status: 'error', error: body.error ?? `HTTP ${res.status}` };
  }
  const body = (await res.json()) as {
    status?: string;
    project?: AdvisorProjectDetail;
    error?: string;
  };
  if (body.status === 'error' || !body.project) {
    return { status: 'error', error: body.error ?? 'project_unavailable' };
  }
  return { status: 'ok', project: body.project };
}

export async function fetchAdvisorBriefFacets(
  builderId: string = DEFAULT_BUILDER,
  signal?: AbortSignal,
): Promise<AdvisorBriefFacetsResponse> {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  const qs = new URLSearchParams({ builder_id: builderId });
  const res = await fetch(`${base}/advisor/brief-facets?${qs}`, { signal });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return { status: 'error', error: body.error ?? `HTTP ${res.status}` };
  }

  return (await res.json()) as AdvisorBriefFacetsResponse;
}

/** A5 — consent reveal → Desk lead on source builder. */
export async function sendAdvisorReveal(input: {
  sessionId: string;
  projectId: string;
  buyerPhone: string;
  buyerName: string;
  visitLabel?: string;
  preferences?: Record<string, string | undefined>;
}): Promise<{
  status: 'ok' | 'error';
  sourceBuilderId?: string;
  sourceProjectId?: string;
  projectName?: string;
  /**
   * The CRM key on the source builder — builder x buyer x PROJECT. The reveal
   * always carries a project_id and writes through Desk `PUT /api/v1/leads`,
   * so what comes back is a lead, never a thread. (Was `conversationId`.)
   */
  leadId?: string;
  created?: boolean;
  error?: string;
}> {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  try {
    const res = await fetch(`${base}/advisor/reveal`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        session_id: input.sessionId,
        project_id: input.projectId,
        buyer_phone: input.buyerPhone,
        buyer_name: input.buyerName,
        ...(input.visitLabel ? { visit_label: input.visitLabel } : {}),
        ...(input.preferences && Object.keys(input.preferences).length
          ? { preferences: input.preferences }
          : {}),
      }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      status?: string;
      source_builder_id?: string;
      source_project_id?: string;
      project_name?: string;
      lead_id?: string;
      /** TRANSITION ONLY — see the note on the turn body above. */
      conversation_id?: string;
      created?: boolean;
      error?: string;
    };
    if (!res.ok || body.status === 'error') {
      return { status: 'error', error: body.error ?? `HTTP ${res.status}` };
    }
    return {
      status: 'ok',
      sourceBuilderId: body.source_builder_id,
      sourceProjectId: body.source_project_id,
      projectName: body.project_name,
      leadId: body.lead_id ?? body.conversation_id,
      created: body.created,
    };
  } catch {
    return { status: 'error', error: 'network' };
  }
}
